import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
    Loader2, Building2, ShieldAlert, ChevronRight, Mail, Phone, MapPin,
    Calendar, Save, Sun, ThermometerSun, Wind, Lightbulb, BarChart3, FileDown,
} from "lucide-react";

const STATUS_FLOW = [
    { value: "new", label: "Nouveau", color: "bg-blue-500" },
    { value: "contacted", label: "Contacté", color: "bg-amber-500" },
    { value: "audit_planned", label: "Audit planifié", color: "bg-violet-500" },
    { value: "won", label: "Gagné", color: "bg-emerald-500" },
    { value: "lost", label: "Perdu", color: "bg-rose-500" },
];

const INTEREST_LABEL = {
    isolation: "Isolation",
    pompe_chaleur: "Pompe à chaleur",
    led: "Éclairage LED",
    ventilation: "Ventilation",
    autoconsommation: "Autoconso. solaire",
    audit: "Audit énergétique",
};
const INTEREST_ICON = {
    isolation: ThermometerSun, pompe_chaleur: Wind, led: Lightbulb,
    ventilation: Wind, autoconsommation: Sun, audit: BarChart3,
};

function StatusBadge({ status }) {
    const conf = STATUS_FLOW.find((s) => s.value === status) || STATUS_FLOW[0];
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold text-white ${conf.color}`} data-testid={`status-badge-${status}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-white" />
            {conf.label}
        </span>
    );
}

export default function AdminCEELeads() {
    const { user, loading: authLoading } = useAuth();
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("all");
    const [selected, setSelected] = useState(null);
    const [newStatus, setNewStatus] = useState("new");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const [s, q] = await Promise.all([
                api.get("/admin/cee-solar/stats"),
                api.get("/admin/cee-solar/leads"),
            ]);
            setStats(s.data);
            setItems(q.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const filtered = filterStatus === "all" ? items : items.filter((i) => i.status === filterStatus);

    const openDetail = (l) => {
        setSelected(l);
        setNewStatus(l.status || "new");
        setNotes("");
    };

    const updateStatus = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            await api.patch(`/admin/cee-solar/leads/${selected.id}/status`, {
                status: newStatus,
                admin_notes: notes || null,
            });
            toast.success(`Statut mis à jour : ${newStatus}`);
            setSelected(null);
            await loadData();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    if (authLoading) return <div className="em-container py-32 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" /></div>;
    if (!user || user.role !== "admin") {
        return (
            <div className="em-container py-20" data-testid="admin-forbidden">
                <ShieldAlert className="h-12 w-12 text-destructive" />
                <h1 className="font-display text-4xl font-black text-primary mt-4">Accès refusé</h1>
                <Button asChild className="rounded-full mt-6 bg-primary hover:bg-primary/90"><Link to="/">Retour à l'accueil</Link></Button>
            </div>
        );
    }

    return (
        <div className="em-container py-8" data-testid="admin-cee-page">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
                <div>
                    <p className="em-eyebrow flex items-center gap-2"><Building2 className="h-3.5 w-3.5" />Administration</p>
                    <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">Leads CEE & Solaire</h1>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={async () => {
                            try {
                                const res = await api.get("/admin/cee-solar/leads/export.csv", { responseType: "blob" });
                                const url = window.URL.createObjectURL(res.data);
                                const a = document.createElement("a");
                                a.href = url; a.download = "energy-market-cee-leads.csv";
                                document.body.appendChild(a); a.click(); a.remove();
                                window.URL.revokeObjectURL(url);
                                toast.success("Export CSV téléchargé");
                            } catch (e) {
                                toast.error("Erreur d'export");
                            }
                        }}
                        type="button"
                        variant="outline"
                        className="rounded-full"
                        data-testid="export-cee-csv"
                    >
                        <FileDown className="h-4 w-4 mr-2" />CSV
                    </Button>
                    <Button
                        onClick={async () => {
                            try {
                                const res = await api.get("/admin/cee-solar/leads/export.xlsx", { responseType: "blob" });
                                const url = window.URL.createObjectURL(res.data);
                                const a = document.createElement("a");
                                a.href = url; a.download = "energy-market-cee-leads.xlsx";
                                document.body.appendChild(a); a.click(); a.remove();
                                window.URL.revokeObjectURL(url);
                                toast.success("Export Excel téléchargé");
                            } catch (e) {
                                toast.error("Erreur d'export");
                            }
                        }}
                        type="button"
                        className="rounded-full bg-primary hover:bg-primary/90 font-bold"
                        data-testid="export-cee-xlsx"
                    >
                        <FileDown className="h-4 w-4 mr-2" />Excel
                    </Button>
                </div>
            </div>
            <p className="text-muted-foreground mt-3 max-w-2xl">Pilotez les demandes d'audit CEE et installations solaires entrantes.</p>

            {/* STATS */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-10" data-testid="cee-stats">
                    <Card className="rounded-2xl border-border md:col-span-2">
                        <CardContent className="p-5 flex items-center gap-3">
                            <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center"><Building2 className="h-6 w-6 text-accent-foreground" /></div>
                            <div>
                                <p className="em-eyebrow">Total</p>
                                <p className="font-display text-3xl font-black text-primary">{stats.total}</p>
                            </div>
                        </CardContent>
                    </Card>
                    {STATUS_FLOW.map((s) => (
                        <Card key={s.value} className="rounded-2xl border-border" data-testid={`cee-stat-${s.value}`}>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2">
                                    <span className={`h-2 w-2 rounded-full ${s.color}`} />
                                    <p className="text-xs em-eyebrow">{s.label}</p>
                                </div>
                                <p className="font-display text-2xl font-black text-primary mt-1">{stats.by_status[s.value] || 0}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* TOP INTERESTS */}
            {stats && stats.top_interests.length > 0 && (
                <Card className="rounded-3xl border-border mt-6" data-testid="cee-interests">
                    <CardContent className="p-6">
                        <p className="em-eyebrow mb-3">Centres d'intérêt les plus demandés</p>
                        <div className="flex flex-wrap gap-2">
                            {stats.top_interests.map((i) => {
                                const Icon = INTEREST_ICON[i.interest] || Building2;
                                return (
                                    <div key={i.interest} className="inline-flex items-center gap-2 bg-secondary rounded-full px-3 py-1.5 text-sm">
                                        <Icon className="h-3.5 w-3.5" />
                                        {INTEREST_LABEL[i.interest] || i.interest}
                                        <span className="font-mono font-bold text-primary">{i.count}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* FILTERS */}
            <div className="mt-8 flex flex-wrap gap-3 items-center" data-testid="cee-filters">
                <Tabs value={filterStatus} onValueChange={setFilterStatus}>
                    <TabsList className="rounded-full">
                        <TabsTrigger value="all" className="rounded-full" data-testid="filter-cee-all">Tous</TabsTrigger>
                        {STATUS_FLOW.map((s) => (
                            <TabsTrigger key={s.value} value={s.value} className="rounded-full" data-testid={`filter-cee-${s.value}`}>{s.label}</TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
                <span className="text-sm text-muted-foreground">{filtered.length} résultat(s)</span>
            </div>

            {/* TABLE */}
            <Card className="rounded-3xl border-border mt-6">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="py-20 text-center text-muted-foreground" data-testid="cee-loading">
                            <Loader2 className="h-8 w-8 mx-auto animate-spin" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="py-16 text-center text-muted-foreground" data-testid="cee-empty">
                            <Building2 className="h-12 w-12 mx-auto text-primary/30" />
                            <p className="mt-3">Aucun lead pour ces filtres.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[820px]" data-testid="cee-leads-table">
                                <thead className="bg-secondary">
                                    <tr className="text-left">
                                        <th className="px-5 py-4 em-eyebrow">Date</th>
                                        <th className="px-5 py-4 em-eyebrow">Demandeur</th>
                                        <th className="px-5 py-4 em-eyebrow">Société</th>
                                        <th className="px-5 py-4 em-eyebrow">Intérêts</th>
                                        <th className="px-5 py-4 em-eyebrow">Toiture</th>
                                        <th className="px-5 py-4 em-eyebrow">Statut</th>
                                        <th className="px-5 py-4"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((l) => (
                                        <tr key={l.id} className="border-t border-border hover:bg-muted/40" data-testid={`cee-row-${l.id}`}>
                                            <td className="px-5 py-4 text-sm">{new Date(l.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</td>
                                            <td className="px-5 py-4">
                                                <p className="font-display font-bold">{l.full_name}</p>
                                                <p className="text-xs text-muted-foreground">{l.email}</p>
                                            </td>
                                            <td className="px-5 py-4 text-sm">{l.company}</td>
                                            <td className="px-5 py-4">
                                                <div className="flex flex-wrap gap-1 max-w-[220px]">
                                                    {(l.interests || []).slice(0, 3).map((i) => (
                                                        <span key={i} className="text-xs bg-secondary px-2 py-0.5 rounded-full">{INTEREST_LABEL[i] || i}</span>
                                                    ))}
                                                    {l.interests && l.interests.length > 3 && (
                                                        <span className="text-xs text-muted-foreground">+{l.interests.length - 3}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 text-sm font-mono">{l.rooftop_m2 ? `${l.rooftop_m2} m²` : "—"}</td>
                                            <td className="px-5 py-4"><StatusBadge status={l.status} /></td>
                                            <td className="px-5 py-4 text-right">
                                                <Button variant="ghost" size="sm" type="button" onClick={() => openDetail(l)} className="rounded-full" data-testid={`open-cee-${l.id}`}>
                                                    Détail <ChevronRight className="h-4 w-4 ml-1" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* DETAIL DIALOG */}
            <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Détail du lead</DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-4" data-testid="cee-detail">
                            <div className="grid grid-cols-2 gap-4">
                                <DetailField icon={Mail} label="Email" value={selected.email} />
                                <DetailField icon={Phone} label="Téléphone" value={selected.phone} />
                                <DetailField icon={Building2} label="Société" value={selected.company} />
                                <DetailField icon={MapPin} label="SIRET" value={selected.siret} />
                                <DetailField icon={Building2} label="Secteur" value={selected.sector} />
                                <DetailField icon={Calendar} label="Reçu le" value={new Date(selected.created_at).toLocaleString("fr-FR")} />
                                <DetailField icon={Building2} label="Employés" value={selected.employees} />
                                <DetailField icon={Building2} label="Conso annuelle" value={selected.annual_kwh ? `${Math.round(selected.annual_kwh).toLocaleString("fr-FR")} kWh` : "—"} />
                                <DetailField icon={Building2} label="Âge bâtiment" value={selected.building_age} />
                                <DetailField icon={Sun} label="Toiture exploitable" value={selected.rooftop_m2 ? `${selected.rooftop_m2} m²` : "—"} />
                            </div>
                            <div>
                                <p className="em-eyebrow mb-2">Intérêts ({selected.interests?.length || 0})</p>
                                <div className="flex flex-wrap gap-2">
                                    {(selected.interests || []).map((i) => (
                                        <span key={i} className="text-xs bg-secondary px-2.5 py-1 rounded-full">{INTEREST_LABEL[i] || i}</span>
                                    ))}
                                </div>
                            </div>
                            {selected.message && (
                                <div className="p-4 bg-secondary rounded-2xl">
                                    <p className="em-eyebrow mb-1">Message</p>
                                    <p className="text-sm">{selected.message}</p>
                                </div>
                            )}
                            <div className="pt-4 border-t border-border space-y-3">
                                <div>
                                    <p className="em-eyebrow mb-2">Mettre à jour le statut</p>
                                    <Select value={newStatus} onValueChange={setNewStatus}>
                                        <SelectTrigger className="rounded-xl" data-testid="cee-status-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STATUS_FLOW.map((s) => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <p className="em-eyebrow mb-2">Notes internes</p>
                                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-xl" placeholder="Suivi commercial, audit planifié pour le…" data-testid="cee-notes" />
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={updateStatus} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="cee-save">
                            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sauvegarde…</> : <><Save className="h-4 w-4 mr-2" />Enregistrer</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function DetailField({ icon: Icon, label, value }) {
    return (
        <div>
            <p className="em-eyebrow flex items-center gap-1.5"><Icon className="h-3 w-3" />{label}</p>
            <p className="text-sm mt-1 break-words">{value || "—"}</p>
        </div>
    );
}
